import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import OwnerSubHeader from "@/components/OwnerSubHeader";
import { Loader2, RefreshCw, AlertTriangle, Layers, Trash2 } from "lucide-react";

/**
 * CustomModules — the mobile app's "Custom Modules" screen.
 * Backend: GET /api/custom-submissions, DELETE /api/admin/custom-submissions/{sid}
 */
export default function CustomModules() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/custom-submissions");
      const arr = Array.isArray(data) ? data : data?.items || [];
      setItems(arr);
    } catch (ex) { setErr(apiError(ex)); setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.form_name || s.name || "submission"}"?`)) return;
    try { await api.delete(`/admin/custom-submissions/${s.id || s._id}`); reload(); }
    catch (ex) { alert(apiError(ex)); }
  };

  return (
    <div className="space-y-5" data-testid="custom-modules-page">
      <OwnerSubHeader
        title="Custom Modules"
        subtitle="Site-specific forms & submissions"
        right={
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        }
      />

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      {items === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading submissions…
        </div>
      ) : items.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">
          <Layers className="w-8 h-8 mx-auto mb-2 opacity-60" />
          No custom submissions yet.
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map((s) => (
            <div key={s.id || s._id} className="panel p-4 flex items-center gap-3" data-testid={`custom-${s.id || s._id}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                   style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                <Layers className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-sm truncate">{s.form_name || s.name || "Submission"}</div>
                <div className="text-[11px] text-muted font-mono-tight mt-0.5 truncate">
                  {s.submitted_by_name || s.author || "—"} · {fmt(s.created_at)} · {s.asset_id || s.site_code || "—"}
                </div>
              </div>
              <button className="btn btn-sm btn-ghost text-red-400" onClick={() => remove(s)}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
